import { useState, useRef } from "react";
import { X } from "lucide-react";
import useClickOutside from "../../hooks/useClickOutside";
import {
  TRIGGER_SHARED_CLS,
  triggerBorderCls,
  DROPDOWN_LIST_CLS,
  rowSepCls,
  rowBgCls,
  DropdownChevron,
  OptionDescription,
} from "./dropdownShared";

const MultiSelect = ({
  value = [],
  onChange,
  options = [],
  placeholder = "Select options",
  error = false,
  className = "",
}) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useClickOutside(ref, () => setOpen(false));

  const toggle = (val) => {
    if (value.includes(val)) {
      onChange(value.filter((v) => v !== val));
    } else {
      onChange([...value, val]);
    }
  };

  const remove = (val, e) => {
    e.stopPropagation();
    onChange(value.filter((v) => v !== val));
  };

  const selectedOptions = options.filter((o) => value.includes(o.value));

  return (
    <div className={`relative ${className}`} ref={ref}>
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className={`min-h-[42px] px-3 py-2 gap-2 ${TRIGGER_SHARED_CLS} ${triggerBorderCls(error)}`}
      >
        <div className="flex flex-wrap gap-1 flex-1">
          {selectedOptions.length === 0 ? (
            <span className="text-ink-gray-400">{placeholder}</span>
          ) : (
            selectedOptions.map((opt) => (
              <span
                key={opt.value}
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-fill-primary/10 text-ink-primary text-xs font-mono font-medium"
              >
                {opt.label}
                <button
                  type="button"
                  onClick={(e) => remove(opt.value, e)}
                  className="hover:text-ink-primary-hover-strong transition-colors"
                  aria-label={`Remove ${opt.label}`}
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            ))
          )}
        </div>
        <DropdownChevron open={open} />
      </button>

      {open && (
        <div role="listbox" aria-multiselectable="true" className={DROPDOWN_LIST_CLS}>
          {options.map((option, i) => {
            const selected = value.includes(option.value);
            return (
              <button
                key={option.value}
                type="button"
                role="option"
                aria-selected={selected}
                onClick={() => toggle(option.value)}
                className={`w-full px-4 py-2.5 text-left flex items-start gap-3 transition-colors ${rowSepCls(i)} ${rowBgCls(selected)}`}
              >
                <span
                  className={`mt-0.5 w-4 h-4 flex-shrink-0 rounded border-2 flex items-center justify-center transition-colors ${
                    selected
                      ? "bg-fill-primary border-edge-primary"
                      : "border-edge-gray-300 dark:border-edge-gray-600"
                  }`}
                >
                  {selected && (
                    <svg className="w-2.5 h-2.5 text-ink-white" viewBox="0 0 10 8" fill="none">
                      <path d="M1 4l3 3 5-6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  )}
                </span>
                <div>
                  <div className={`font-mono font-medium text-xs ${selected ? "text-ink-primary" : "text-ink-ink dark:text-ink-white"}`}>
                    {option.label}
                  </div>
                  <OptionDescription text={option.description} />
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default MultiSelect;
